import React, { useEffect, useState } from 'react';
import { fetchShiftsList, updateShift, fetchPortsList } from './services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Form, Card, Alert, Container, Spinner } from 'react-bootstrap';
import { Shift, Port } from './types';
import { useAuth } from './AuthContext';

const EditShift: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shift, setShift] = useState<Shift | null>(null);
  const [name, setName] = useState('');
  const [port, setPort] = useState<number | ''>('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [duration, setDuration] = useState('');
  const [ports, setPorts] = useState<Port[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [shiftsResponse, portsResponse] = await Promise.all([
          fetchShiftsList(),
          fetchPortsList()
        ]);

        const shifts = Array.isArray(shiftsResponse.data) ? shiftsResponse.data : [];
        const ports = Array.isArray(portsResponse.data) ? portsResponse.data : [];

        const found = shifts.find((s: Shift) => s.id === Number(id));
        if (!found) throw new Error('Shift not found');

        setShift(found);
        setName(found.name);
        setPort(found.port);
        setStartTime(found.start_time);
        setEndTime(found.end_time);
        setDuration(found.duration_hours.toString());
        setPorts(ports);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load shift');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateShift(Number(id), {
        name,
        port: Number(port),
        start_time: startTime,
        end_time: endTime,
        duration_hours: Number(duration)
      });
      setSuccess(true);
      setTimeout(() => navigate('/shifts'), 1200);
    } catch (err) {
      console.error('Error updating shift:', err);
      setError('Failed to update shift');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}><Spinner animation="border" /></div>;
  if (error) return <Container className="p-4 p-md-5"><Alert variant="danger">{error}</Alert></Container>;
  if (!shift) return <Container className="p-4 p-md-5">Shift not found.</Container>;

  return (
    <Container className="p-4 p-md-5" style={{ maxWidth: 500 }}>
      <Card className="shadow-lg mt-4">
        <Card.Body>
          <h2 className="mb-4">Edit Shift</h2>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={name} onChange={e => setName(e.target.value)} required maxLength={50} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="port">
              <Form.Label>Location</Form.Label>
              <Form.Select value={port} onChange={e => setPort(e.target.value === '' ? '' : Number(e.target.value))} required disabled={!!(token && token.designation?.user_type === 'ADMIN')}>
                <option value="">Select Location</option>
                {ports.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="startTime">
              <Form.Label>Start Time</Form.Label>
              <Form.Control type="time" value={startTime} onChange={e => setStartTime(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3" controlId="endTime">
              <Form.Label>End Time</Form.Label>
              <Form.Control type="time" value={endTime} onChange={e => setEndTime(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-4" controlId="duration">
              <Form.Label>Duration (hours)</Form.Label>
              <Form.Control type="number" value={duration} onChange={e => setDuration(e.target.value)} required min={1} />
            </Form.Group>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">Shift updated!</Alert>}
            <div className="d-flex justify-content-between">
              <Button type="button" variant="secondary" onClick={() => navigate('/shifts')} disabled={saving}>Back to List</Button>
              <Button type="submit" variant="primary" disabled={saving}>{saving ? <Spinner size="sm" animation="border" /> : 'Save Changes'}</Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EditShift; 